import React, { useState, useEffect } from 'react';
import Button from './figma/Button';

function Onboarding({ reseller, onComplete }) {
  const [onboardingUrl, setOnboardingUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [country, setCountry] = useState('US');
  const [error, setError] = useState(false);

  const createStripeAccount = async () => {
    setLoading(true);
    setError(false);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      
      // Check if account already exists
      if (reseller.stripe_connect_account_id) {
        // Account exists, create new onboarding link
        const linkResponse = await fetch(`${apiUrl}/api/connect/create-onboarding-link`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ connect_account_id: reseller.stripe_connect_account_id })
        });

        if (linkResponse.ok) {
          const { onboarding_url } = await linkResponse.json();
          setOnboardingUrl(onboarding_url);
        }
      } else {
        // Create new Stripe Connect account
        const accountResponse = await fetch(`${apiUrl}/api/connect/create-account`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            reseller_id: reseller.reseller_id,
            email: reseller.email,
            business_name: reseller.business_name,
            country: country
          })
        });

        if (accountResponse.ok) {
          const { connect_account_id } = await accountResponse.json();
          
          // Create onboarding link
          const linkResponse = await fetch(`${apiUrl}/api/connect/create-onboarding-link`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ connect_account_id })
          });

          if (linkResponse.ok) {
            const { onboarding_url } = await linkResponse.json();
            setOnboardingUrl(onboarding_url);
          }
        }
      }
    } catch (error) {
      console.warn('Onboarding API not yet available:', error.message);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  // Removed auto-trigger - user must select country first

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="bg-white p-12 rounded-2xl shadow-xl max-w-2xl w-full">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Complete Your Partner Setup
          </h1>
          <p className="text-gray-600">
            Welcome to the Remote Assistant Partner Program! 
            Complete your Stripe Connect setup to start earning revenue.
          </p>
        </div>

        <div className="bg-gray-50 rounded-xl p-6 mb-6">
          <div className="space-y-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900">Partner Account Created</p>
                <p className="text-sm text-gray-500">Business: {reseller?.business_name}</p>
              </div>
            </div>

            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <div className="w-3 h-3 bg-blue-600 rounded-full"></div>
                </div>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900">Payment Setup Required</p>
                <p className="text-sm text-gray-500">Complete Stripe Connect onboarding</p>
              </div>
            </div>
          </div>

          <div className="mt-6">
            {!onboardingUrl && !loading && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Your Country
                  </label>
                  <select
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="US">United States</option>
                    <option value="CA">Canada</option>
                  </select>
                </div>
                <div className="text-center">
                  <Button
                    onClick={createStripeAccount}
                    variant="primary"
                  >
                    Start Payment Setup
                  </Button>
                </div>
              </div>
            )}
            {loading ? (
              <div className="text-center py-4">
                <div className="text-lg">Setting up your payment account...</div>
              </div>
            ) : onboardingUrl ? (
              <div className="text-center">
                <Button
                  onClick={() => window.location.href = onboardingUrl}
                  variant="primary"
                >
                  Complete Stripe Setup
                </Button>
                <p className="mt-2 text-sm text-gray-500">
                  You'll be redirected to Stripe to complete your payment setup
                </p>
              </div>
            ) : error ? (
              <div className="text-center text-red-600">
                Error setting up payment account. Please try again.
              </div>
            ) : null}
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <h3 className="text-lg font-medium text-blue-900">What's Next?</h3>
          <ul className="mt-2 text-sm text-blue-700 space-y-1">
            <li>• Complete Stripe Connect verification</li>
            <li>• Access your partner dashboard</li>
            <li>• Generate customer referral links</li>
            <li>• Track your revenue and commissions</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default Onboarding;